import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import { useRecordContext } from "react-admin";
import { Controller, useForm } from "react-hook-form";
import sendRequest from "../../dataProvider/sendRequest";
import CustomEditRemoveIcon from "../../unit/CustomEditRemoveIcon";
import CustomProductCheckboxGroup from "../../unit/forms/CustomProductCheckboxGroup";
import CustomProductIntTextField from "../../unit/forms/CustomProductIntTextField";
import CustomProductSelect from "../../unit/forms/CustomProductSelect";
import CustomProductTextField from "../../unit/forms/CustomProductTextField";
import FieldTitle from "./FieldTitle";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import StateModal from "./StateModal";
import CustomProductActionBottom from "../CustomProductActionBottom";
import { useWatch } from "react-hook-form";
import customSortArrays from "../../unit/sortArrays";
import CircularProgress from "@mui/material/CircularProgress";
const ATTR_GROUP_TITLES = {
  ID: "Permissible ID Type",
  "Business Cash Flows": "Gross Revenue, Primary Expenses Operating Income",
  "Entry Type": "Entry Type",
  "Cash Flows":
    "Monthly Gross Earned and Unearned Income, Major Spending Amount Itemized",
} as Record<string, string>;

const normalizeDelimitedValues = (value: any) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (typeof value !== "string") {
    return [];
  }
  return value
    .split(/[|,]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const ensureRequiredAttrEntries = (fieldKey: any, value: any) => {
  const entries = typeof value === "string" && value ? value.split(",") : [];
  if (fieldKey !== "base_personal_info") {
    return entries.join(",");
  }

  const nextEntries = [...entries];
  ["Name|Required", "Birth Date|Required"].forEach((requiredEntry) => {
    const [requiredName] = requiredEntry.split("|");
    const existingIndex = nextEntries.findIndex((item) =>
      item.startsWith(`${requiredName}|`)
    );
    if (existingIndex >= 0) {
      nextEntries[existingIndex] = requiredEntry;
    } else {
      nextEntries.push(requiredEntry);
    }
  });

  return nextEntries.join(",");
};

const ensureRequiredIdOptions = (value: any) => {
  const ids = normalizeDelimitedValues(value);
  if (!ids.includes("ssn")) {
    ids.unshift("ssn");
  }
  return Array.from(new Set(ids));
};

const getAttrGroupTitle = (attrgroup: any, division: any) => {
  if (ATTR_GROUP_TITLES[attrgroup]) {
    return ATTR_GROUP_TITLES[attrgroup];
  }

  if (attrgroup === "Assets") {
    return division === "Business"
      ? "Cash, Investment, Accounts Receivables, Equipment and Real Estate value"
      : "Cash and Securities Account Current Value";
  }

  if (attrgroup === "Liabilities") {
    return division === "Business"
      ? "Accounts Payables and Loans"
      : "Credit Card Balance, Loans, Mortgages Currently Outstanding";
  }

  if (attrgroup === "Staff/Work Force") {
    return "Number of Full-Time, Part-Time Employees and Consultants";
  }

  if (attrgroup === "Stores/Branches") {
    return "Annual sales at each store or branch in the last 12 months";
  }

  if (attrgroup === "Customers") {
    return "Customers that contribute to more than 20% in annual sales in the last 12 months";
  }

  if (attrgroup === "Suppliers") {
    return "Suppliers that account for to more than 30% in annual COGS in the last 12 months";
  }

  return null;
};

const StableShowAttrGroupTitle = ({ attrgroup, group, division }: any) => {
  const title = getAttrGroupTitle(attrgroup, division);
  if (title) return <span>{title}</span>;

  const showGroupTitles = Object.entries(group as Record<string, any>)
    .filter(
      ([_, one]) =>
        one?.name !== "_sBusinessNAICS_note" &&
        one?.name !== "__contact_info" &&
        one?.name !== "__entry_type"
    )
    .map(([_, one]) => one?.title);

  return showGroupTitles.length > 0 ? (
    <span>{showGroupTitles.join(", ")}</span>
  ) : null;
};

const StableDataFieldsBoxTable = ({
  attrgroupKey,
  attrgroupDefaultAttrs,
  attributeData,
  control,
  division,
  getValues,
  setValue,
  data,
}: any) => {
  const isPrimaryBasicPersonalInfo = attrgroupKey === "base_personal_info";
  let defaultAttrgroupDefaultAttrs: any[] | (() => any[]) = [];
  if (attrgroupDefaultAttrs) {
    defaultAttrgroupDefaultAttrs = attrgroupDefaultAttrs.split(",");
  }
  const [dataResourceAttrs, setDataResourceAttrs] = React.useState(
    getValues(`dataResourceAttrs_${attrgroupKey}`)?.split(",") ||
      defaultAttrgroupDefaultAttrs
  );
  const [idOptionsList, setIdOptionsList] = React.useState(
    ensureRequiredIdOptions(data?.ID_options_list)
  );
  const [entryTypeOptionsList, setEntryTypeOptionsList] = React.useState(
    data?.entrytype_options_list?.split(",") || []
  );

  const handleAttrChange = (attrgroup: any, checked: any) => {
    const newDataResourceAttrs = dataResourceAttrs.filter(
      (item: any) => !item.startsWith(attrgroup)
    );
    if (checked) {
      const attrValue = getValues(
        `primary_dataresources_${attrgroupKey}_required_${attrgroup}`
      );
      newDataResourceAttrs.push(`${attrgroup}|${attrValue}`);
    }
    setDataResourceAttrs(newDataResourceAttrs);
    setValue(
      `dataResourceAttrs_${attrgroupKey}`,
      newDataResourceAttrs.join(",")
    );

    if (
      newDataResourceAttrs.length === 1 &&
      newDataResourceAttrs[0].startsWith("default")
    ) {
      setValue(`primary_dataResourceName_${attrgroupKey}`, false);
    }

    if (
      newDataResourceAttrs.length > 1 &&
      newDataResourceAttrs.some((item: any) => !item.startsWith("default"))
    ) {
      setValue(`primary_dataResourceName_${attrgroupKey}`, true);
    }
  };

  const handleChangeIDOptions = (selected: any) => {
    const requiredSelected = ensureRequiredIdOptions(selected);
    setValue("ID_options_selecte", requiredSelected.join(","), {
      shouldDirty: true,
      shouldTouch: true,
    });
    setIdOptionsList(requiredSelected);
  };

  const handleChangeEntryTypeOptions = (selected: any) => {
    setValue("entrytype_options_selecte", selected, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setEntryTypeOptionsList(selected?.split(","));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        minWidth: 300,
        width: "100%",
        maxWidth: "100%",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "80px" }}>Select</TableCell>
            <TableCell sx={{ width: "80px" }}>Group</TableCell>
            <TableCell sx={{}}>Attributes</TableCell>
            <TableCell sx={{ width: "10%" }}>Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(attributeData as Record<string, any>).map(
            ([attrgroup, group]) => {
              const isRequiredAttr =
                isPrimaryBasicPersonalInfo &&
                (attrgroup == "Birth Date" || attrgroup == "Name");
              if (
                !attrgroup ||
                (attrgroup == "default" && group?.title !== "Financial Info")
              ) {
                return null;
              }

              return (
                <TableRow
                  key={attrgroup}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: "#fff",
                  }}
                >
                  <TableCell>
                    <Controller
                      name={`primary_dataresources_${attrgroupKey}_attrs_${attrgroup}`}
                      control={control}
                      defaultValue={
                        isRequiredAttr ||
                        dataResourceAttrs?.some((item: any) =>
                          item.startsWith(attrgroup)
                        )
                      }
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={(e) => {
                                if (isRequiredAttr && !e.target.checked) {
                                  e.target.checked = true;
                                }
                                field.onChange(e.target.checked);
                                if (e.target.checked) {
                                  setValue(
                                    `primary_dataresources_${attrgroupKey}_required_${attrgroup}`,
                                    "Required"
                                  );
                                } else {
                                  setValue(
                                    `primary_dataresources_${attrgroupKey}_required_${attrgroup}`,
                                    ""
                                  );
                                }
                                handleAttrChange(attrgroup, e.target.checked);
                              }}
                              disabled={isRequiredAttr}
                            />
                          }
                          label={`${attrgroup}`}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {attrgroup !== "default" &&
                      (attrgroup === "Business Cash Flows"
                        ? "Cash Flows"
                        : attrgroup)}
                  </TableCell>
                  <TableCell>
                    <StableShowAttrGroupTitle
                      attrgroup={attrgroup}
                      group={group}
                      division={division}
                    />
                    <Box>
                      {attrgroup == "ID" && (
                        <CustomProductCheckboxGroup
                          selected={idOptionsList}
                          handleChange={handleChangeIDOptions}
                          requiredSelectedIds={["ssn"]}
                          optionlist={[
                            {
                              id: "ssn",
                              label: "Social Security Number (SSN)",
                            },
                            {
                              id: "tin",
                              label: "Tax Identification Number (TIN)",
                            },
                            { id: "driver", label: "Driver License" },
                            {
                              id: "employmentauth",
                              label:
                                "Employment Authorization Document (Form I-766/EAD)",
                            },
                            {
                              id: "traveldocument",
                              label: "Form I-94 Travel Document",
                            },
                            { id: "passprot", label: "USA Passport" },
                            {
                              id: "non-usa-passport",
                              label: "Non-USA Passport",
                            },
                            { id: "studentid", label: "Student ID" },
                            {
                              id: "idcard",
                              label: "State or Municipal ID",
                            },
                            {
                              id: "greencard",
                              label: "US Permanent Resident Card (Green Card)",
                            },
                          ]}
                        />
                      )}
                      {attrgroup == "Entity Type" && (
                        <CustomProductCheckboxGroup
                          selected={entryTypeOptionsList}
                          handleChange={handleChangeEntryTypeOptions}
                          optionlist={[
                            { id: "partnership", label: "Partnership" },
                            { id: "ccorp", label: "C-Corp" },
                            { id: "scorp", label: "S-Corp" },
                            { id: "llc", label: "LLC" },
                            {
                              id: "nonprofit",
                              label: "501(c)(3) nonprofit",
                            },
                            {
                              id: "veterans",
                              label: "501(c)(19) veterans organization",
                            },
                            {
                              id: "tribal",
                              label:
                                "Tribal business (sec. 31(b)(2)(C) of Small Business Act)",
                            },
                            { id: "sole", label: "Sole Proprietorship" },
                            {
                              id: "contractor",
                              label: "Independent Contractor",
                            },
                            {
                              id: "ownerllc",
                              label: "Single-Owner LLC",
                            },
                          ]}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Controller
                      name={`primary_dataresources_${attrgroupKey}_required_${attrgroup}`}
                      control={control}
                      defaultValue={
                        isRequiredAttr
                          ? "Required"
                          : dataResourceAttrs?.some((item: any) =>
                              item.startsWith(attrgroup)
                            )
                          ? dataResourceAttrs
                              .find((item: any) => item.startsWith(attrgroup))
                              ?.split("|")[1]
                          : ""
                      }
                      render={({ field }) => (
                        <CustomProductSelect
                          {...field}
                          disabled={isRequiredAttr}
                          optionlist={[
                            { value: "", label: "Select" },
                            { value: "Required", label: "Required" },
                            { value: "Optional", label: "Optional" },
                          ]}
                          onChange={(e) => {
                            if (isRequiredAttr) {
                              field.onChange("Required");
                              handleAttrChange(attrgroup, true);
                              return;
                            }
                            field.onChange(e);
                            if (
                              e.target.value === "Required" ||
                              e.target.value == "Optional"
                            ) {
                              setValue(
                                `primary_dataresources_${attrgroupKey}_attrs_${attrgroup}`,
                                true
                              );
                              handleAttrChange(attrgroup, true);
                            } else {
                              setValue(
                                `primary_dataresources_${attrgroupKey}_attrs_${attrgroup}`,
                                false
                              );
                            }
                          }}
                          small
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const StableDataFieldGrid = ({
  fieldKey: key,
  value,
  dataType,
  division,
  control,
  owner = "no",
  data,
  getValues,
  setValue,
}: any) => {
  const [showDetail, setShowDetail] = React.useState(false);
  const [selectState, setSelectState] = React.useState(
    normalizeDelimitedValues(data?.statesData)
  );
  const [fieldGroups, setFieldGroups] = React.useState([]);
  const isRequiredPrimaryBasicPersonalInfo =
    owner !== "yes" && key === "base_personal_info";
  const normalizedAttrGroupValue = ensureRequiredAttrEntries(
    key,
    value?.attrsgroup
  );
  const dataResourceFieldName =
    owner == "yes"
      ? `owner_dataResourceName_${key}`
      : `primary_dataResourceName_${key}`;
  const defaultSelected =
    isRequiredPrimaryBasicPersonalInfo ||
    (division == "Business"
      ? value?.business_selected === "yes" || value?.owner_selected === "yes"
      : value?.myinfo_selected === "yes");
  const isDetailEnabled = useWatch({
    control,
    name: dataResourceFieldName,
    defaultValue: defaultSelected,
  });

  const { title = "", desc: description = "", appto = [] } = value || {};

  if (division != "Business") {
    if (
      dataType === "myinfo" &&
      !appto.includes("myinfo") &&
      !appto.includes("business")
    ) {
      return null;
    }
  }
  if (division === "Business" && key === "householdinfo") {
    return null;
  }

  const handleGetFieldGroups = async () => {
    const response = await sendRequest(
      `getDataTypeFields/primaryApplicant/${key}`,
      {
        id: 0,
      }
    );
    if (response?.code === 200) {
      setFieldGroups(response?.attributeData || []);
    }
  };

  const toggleDetail = async () => {
    if (!showDetail) {
      await handleGetFieldGroups();
      setShowDetail(true);
    } else {
      setShowDetail(false);
    }
  };

  React.useEffect(() => {
    if (!isDetailEnabled && showDetail) {
      setShowDetail(false);
    }
  }, [isDetailEnabled, showDetail]);

  const handleChangeState = (selected: any) => {
    const normalizedStates = normalizeDelimitedValues(selected);
    setSelectState(normalizedStates);
    setValue("states", normalizedStates.join("|"), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const renderFieldSpecificContent = () => {
    switch (key) {
      case "address":
        return (
          <>
            Residential Address for the most recent
            <Controller
              name="min_month_address"
              control={control}
              defaultValue={data?.iMinMonthsAddress ?? 0}
              render={({ field }) => (
                <CustomProductIntTextField {...field} small />
              )}
            />
            months <br />
            Permissible States :
            <StateModal
              selected={selectState}
              handleChange={handleChangeState}
            />
            <Controller
              name="states"
              control={control}
              defaultValue={normalizeDelimitedValues(data?.statesData).join(
                "|"
              )}
              render={({ field }) => (
                <CustomProductTextField {...field} readOnly={true} />
              )}
            />
          </>
        );
      case "employmentinfo":
        return (
          <>
            Employment Status, Employer and Employment Income Info for the most
            recent
            <Controller
              name="min_months_employment"
              control={control}
              defaultValue={data?.iMinMonthsEmployment ?? 0}
              render={({ field }) => (
                <CustomProductIntTextField {...field} small />
              )}
            />
            months
          </>
        );
      case "reference":
        return (
          <>
            <div style={{ display: "inline-block", marginRight: "10px" }}>
              <Controller
                name="referral"
                control={control}
                defaultValue={data?.iNumReferrals ?? 0}
                render={({ field }) => (
                  <CustomProductIntTextField {...field} small />
                )}
              />
            </div>
            Minimum number of references required
          </>
        );
      default:
        return (
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        );
    }
  };

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{ borderBottom: "1px solid #ccc", paddingBottom: "10px" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={1}>
            <Controller
              name={dataResourceFieldName}
              control={control}
              defaultValue={defaultSelected}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={isRequiredPrimaryBasicPersonalInfo}
                      onChange={(e) => {
                        if (
                          isRequiredPrimaryBasicPersonalInfo &&
                          !e.target.checked
                        ) {
                          field.onChange(true);
                          return;
                        }
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          setShowDetail(false);
                        }
                      }}
                    />
                  }
                  label=""
                />
              )}
            />
          </Grid>
          <Grid item xs={3}>
            <FieldTitle title={title} />
          </Grid>
          <Grid item xs={7}>
            {renderFieldSpecificContent()}
          </Grid>
          <Grid item xs={1}>
            {key != "referralinfo" &&
              key != "financial_education_verification" && (
                <>
                  {isDetailEnabled ? (
                    <IconButton onClick={toggleDetail}>
                      {showDetail ? (
                        <ExpandMoreIcon sx={{ color: "#1976d2" }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ color: "#1976d2" }} />
                      )}
                    </IconButton>
                  ) : (
                    <IconButton>
                      <Tooltip
                        title="Please select the checkbox to view the details"
                        placement="left"
                      >
                        <KeyboardArrowRightIcon sx={{ color: "grey" }} />
                      </Tooltip>
                    </IconButton>
                  )}
                </>
              )}
            <Controller
              name={`dataResourceAttrs_${key}`}
              control={control}
              defaultValue={normalizedAttrGroupValue}
              render={({ field }) => <input type="hidden" {...field} />}
            />
            <Controller
              name={`dataResourceAttrs_${key}_allRequired`}
              control={control}
              defaultValue={value?.attrsgroupAllRequired}
              render={({ field }) => <input type="hidden" {...field} />}
            />
          </Grid>
        </Grid>
        <Collapse in={showDetail} timeout="auto" unmountOnExit>
          <Grid container>
            <Grid item xs={4}></Grid>
            <Grid item xs={7}>
              <StableDataFieldsBoxTable
                attrgroupKey={key}
                attrgroupDefaultAttrs={normalizedAttrGroupValue}
                attributeData={fieldGroups}
                control={control}
                division={division}
                getValues={getValues}
                setValue={setValue}
                data={data}
              />
            </Grid>
            <Grid item xs={1}></Grid>
          </Grid>
        </Collapse>
      </Grid>
    </>
  );
};

export default function DataFields(
  //   {
  //   onSave,
  //   defaultValues,
  //   curMenu,
  //   onDirtyChange,
  // }: any
  { ...props }
) {
  const normalizeDelimitedValues = (value: any) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }
    if (typeof value !== "string") {
      return [];
    }
    return value
      .split(/[|,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const ensureRequiredAttrEntries = (fieldKey: any, value: any) => {
    const entries = typeof value === "string" && value ? value.split(",") : [];
    if (fieldKey !== "base_personal_info") {
      return entries.join(",");
    }

    const nextEntries = [...entries];
    ["Name|Required", "Birth Date|Required"].forEach((requiredEntry) => {
      const [requiredName] = requiredEntry.split("|");
      const existingIndex = nextEntries.findIndex((item) =>
        item.startsWith(`${requiredName}|`)
      );
      if (existingIndex >= 0) {
        nextEntries[existingIndex] = requiredEntry;
      } else {
        nextEntries.push(requiredEntry);
      }
    });

    return nextEntries.join(",");
  };

  const ensureRequiredIdOptions = (value: any) => {
    const ids = normalizeDelimitedValues(value);
    if (!ids.includes("ssn")) {
      ids.unshift("ssn");
    }
    return Array.from(new Set(ids));
  };

  const record = useRecordContext();
  const data = record?.data?.basic?.datafields;
  const questionData = data?.questionData?.attrData || [];
  const division = record?.data?.basic?.division;
  const productType = record?.data?.basic?.type;
  const dataResourceSettings = data?.dataResourceSettings || {};
  const [isActualDirty, setIsActualDirty] = React.useState(false);
  const [initialValuesSet, setInitialValuesSet] = React.useState({});
  const [isSaving, setIsSaving] = React.useState(false);
  let guarantor_base_personal_info_selected = "no";
  let guarantor_address_selected = "no";
  let guarantor_employmentinfo_selected = "no";
  let guarantor_reference_selected = "no";
  let coapp_base_personal_info_selected = "no";
  let coapp_address_selected = "no";
  let coapp_employmentinfo_selected = "no";
  let coapp_reference_selected = "no";
  let owner_base_personal_info_selected = "no";
  let owner_address_selected = "no";
  let owner_financialinfo_selected = "no";
  let owner_demographicinfo_selected = "no";
  let owner_immigrationinfo_selected = "no";

  Object.entries(dataResourceSettings as Record<string, any>).map(
    ([key, value]) => {
      if (
        key === "base_personal_info" &&
        value?.coapplicant_selected === "yes"
      ) {
        coapp_base_personal_info_selected = "yes";
      }
      if (key === "address" && value?.coapplicant_selected === "yes") {
        coapp_address_selected = "yes";
      }
      if (key === "employmentinfo" && value?.coapplicant_selected === "yes") {
        coapp_employmentinfo_selected = "yes";
      }
      // if (key === "reference" && value?.myinfo_selected === "yes") {
      //   coapp_reference_selected = "yes";
      // }
      if (key === "base_personal_info" && value?.guarantor_selected === "yes") {
        guarantor_base_personal_info_selected = "yes";
      }
      if (key === "address" && value?.guarantor_selected === "yes") {
        guarantor_address_selected = "yes";
      }
      if (key === "employmentinfo" && value?.guarantor_selected === "yes") {
        guarantor_employmentinfo_selected = "yes";
      }
      if (key === "reference" && value?.guarantor_selected === "yes") {
        guarantor_reference_selected = "yes";
      }
    }
  );

  let defaultGroupCoApplicant = [];
  let defaultGroupGuarantor = [];
  defaultGroupCoApplicant.push({
    key: "base_personal_info",
    title: "Basic Personal Info",
    desc: "Legal Name, Birth Date, Government Issued ID, Phone, Email, and Preferred Method of Communication",
    coapp_selected: coapp_base_personal_info_selected,
    guarantor_selected: guarantor_base_personal_info_selected,
  });
  defaultGroupCoApplicant.push({
    key: "address",
    title: "Address Info",
    desc: "same as Primary Applicant",
    coapp_selected: coapp_address_selected,
    guarantor_selected: guarantor_address_selected,
  });
  defaultGroupCoApplicant.push({
    key: "employmentinfo",
    title: "Employment Info",
    desc: "same as Primary Applicant",
    coapp_selected: coapp_employmentinfo_selected,
    guarantor_selected: guarantor_employmentinfo_selected,
  });

  defaultGroupGuarantor.push({
    key: "base_personal_info",
    title: "Basic Personal Info",
    desc: "Legal Name, Birth Date, Government Issued ID, Phone, Email, and Preferred Method of Communication",
    coapp_selected: coapp_base_personal_info_selected,
    guarantor_selected: guarantor_base_personal_info_selected,
  });
  defaultGroupGuarantor.push({
    key: "address",
    title: "Address Info",
    desc: "same as Primary Applicant",
    coapp_selected: coapp_address_selected,
    guarantor_selected: guarantor_address_selected,
  });
  defaultGroupGuarantor.push({
    key: "employmentinfo",
    title: "Employment Info",
    desc: "same as Primary Applicant",
    coapp_selected: coapp_employmentinfo_selected,
    guarantor_selected: guarantor_employmentinfo_selected,
  });
  defaultGroupGuarantor.push({
    key: "reference",
    title: "Reference",
    desc: "same as Primary Applicant",
    coapp_selected: coapp_reference_selected,
    guarantor_selected: guarantor_reference_selected,
  });

  const sectionName = "datafields";
  const sectionTitle =
    "Data Fields - Describe information to be supplied by applicant";
  const sectionSubtitle = "";

  // const buildInitialValues = () => {
  //   if (data) {
  //     return {
  //       min_month_address: data?.iMinMonthsAddress || "",
  //       min_months_employment: data?.iMinMonthsEmployment || "",
  //       referral: data?.iNumReferrals || "",
  //       cosigner: data?.sCoSigner || "",
  //       guarantor: data?.sGuarantor || "",
  //       sQuestion: data?.sQuestion || "NO",
  //       states: data?.statesData || "",
  //       dataResourceName: data?.dataResourceName || [],
  //       coapp_dataResourceName: data?.coapp_dataResourceName || [],
  //       guarantors_dataResourceName: data?.guarantors_dataResourceName || [],
  //       ID_options_selecte: data?.ID_options_list || [],
  //       entrytype_options_selecte: data?.entrytype_options_list || [],
  //     };
  //   }
  //   // return {
  //   //   min_month_address: "",
  //   //   min_months_employment: "",
  //   //   referral: "",
  //   //   cosigner: "NO",
  //   //   guarantor: "NO",
  //   //   sQuestion: "NO",
  //   //   states: "",
  //   //   dataResourceName: [],
  //   //   coapp_dataResourceName: [],
  //   //   guarantors_dataResourceName: [],
  //   //   ID_options_selecte: [],
  //   //   entrytype_options_selecte: [],
  //   // };
  // };

  // const initialValues = buildInitialValues();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<any>({
    mode: "onChange",
    // defaultValues: props?.defaultValues || {},
    defaultValues: {},
  });
  const coSignerAccepted = (watch as any)("cosigner") === "YES";
  const guarantorAccepted = (watch as any)("guarantor") === "YES";
  const defaultValuesRef = React.useRef<any>(null); // 1. 保存真正的初始值
  const hasSetBaseline = React.useRef(false);

  React.useEffect(() => {
    // 使用 requestAnimationFrame 确保在渲染后执行
    const rafId = requestAnimationFrame(() => {
      if (!hasSetBaseline.current) {
        const currentValues = getValues();
        setInitialValuesSet(currentValues);
        reset(currentValues);
        hasSetBaseline.current = true;
        console.log("Check 首次渲染基准已设置====》", currentValues);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [getValues, reset]);

  React.useEffect(() => {
    if (props?.onDirtyChange) {
      props?.onDirtyChange(props?.curMenu, isDirty);
    }
  }, [isDirty, reset]);

  React.useEffect(() => {
    if (coSignerAccepted) {
      (setValue as any)("coapp_dataResourceName_base_personal_info", true);
    }
  }, [coSignerAccepted, setValue]);

  React.useEffect(() => {
    if (guarantorAccepted) {
      (setValue as any)("guarantor_dataResourceName_base_personal_info", true);
    }
  }, [guarantorAccepted, setValue]);
  // }, [isActualDirty, reset]);

  const getAttrGroupTitle = (attrgroup: any, division: any) => {
    if (ATTR_GROUP_TITLES[attrgroup]) {
      return ATTR_GROUP_TITLES[attrgroup];
    }

    if (attrgroup === "Assets") {
      return division === "Business"
        ? "Cash, Investment, Accounts Receivables, Equipment and Real Estate value"
        : "Cash and Securities Account Current Value";
    }

    if (attrgroup === "Liabilities") {
      return division === "Business"
        ? "Accounts Payables and Loans"
        : "Credit Card Balance, Loans, Mortgages Currently Outstanding";
    }

    if (attrgroup === "Staff/Work Force") {
      return "Number of Full-Time, Part-Time Employees and Consultants";
    }

    if (attrgroup === "Stores/Branches") {
      return "Annual sales at each store or branch in the last 12 months";
    }

    if (attrgroup === "Customers") {
      return "Customers that contribute to more than 20% in annual sales in the last 12 months";
    }

    if (attrgroup === "Suppliers") {
      return "Suppliers that account for to more than 30% in annual COGS in the last 12 months";
    }

    return null;
  };

  const ShowAttrGroupTitle = ({ attrgroup, group, division }: any) => {
    const title = getAttrGroupTitle(attrgroup, division);
    if (title) return <span>{title}</span>;

    const showGroupTitles = Object.entries(group as Record<string, any>)
      .filter(
        ([_, one]) =>
          one?.name !== "_sBusinessNAICS_note" &&
          one?.name !== "__contact_info" &&
          one?.name !== "__entry_type"
      )
      .map(([_, one]) => one?.title);

    return showGroupTitles.length > 0 ? (
      <span>{showGroupTitles.join(", ")}</span>
    ) : null;
  };

  const DataFieldsBoxTable = ({
    attrgroupKey,
    attrgroupDefaultAttrs,
    attrgroupDefaultAllRequiredAttrs,
    attributeData,
    control,
    setValue,
  }: any) => {
    const isPrimaryBasicPersonalInfo = attrgroupKey === "base_personal_info";
    let default_attrgroupDefaultAttrs: any[] | (() => any[]) = [];
    if (attrgroupDefaultAttrs) {
      default_attrgroupDefaultAttrs = attrgroupDefaultAttrs.split(",");
    }
    const [dataResourceAttrs, setDataResourceAttrs] = React.useState(
      getValues(`dataResourceAttrs_${attrgroupKey}`)?.split(",") ||
        default_attrgroupDefaultAttrs
    );

    //设置一个方法， 保持数据default|Required,Contact Info|Optional,Name|Required,Birth Date|Required,ID|Required,
    const handleAttrChange = (attrgroup: any, checked: any) => {
      const newDataResourceAttrs = dataResourceAttrs.filter(
        (item: any) => !item.startsWith(attrgroup)
      );
      if (checked) {
        let attrValue = getValues(
          `primary_dataresources_${attrgroupKey}_required_${attrgroup}`
        );
        newDataResourceAttrs.push(`${attrgroup}|${attrValue}`);
      }
      setDataResourceAttrs(newDataResourceAttrs);
      setValue(
        `dataResourceAttrs_${attrgroupKey}`,
        newDataResourceAttrs.join(",")
      );

      //如果newDataResourceAttrs中只有default|Required, 则设置设置primary_dataResourceName_attrgroup为false
      if (
        newDataResourceAttrs.length === 1 &&
        newDataResourceAttrs[0].startsWith("default")
      ) {
        setValue(`primary_dataResourceName_${attrgroupKey}`, false);
      }

      //如果有一个非default的选中，并且则设置设置primary_dataResourceName_attrgroup为false， 则设置其是true
      if (
        newDataResourceAttrs.length > 1 &&
        newDataResourceAttrs.some((item: any) => !item.startsWith("default"))
      ) {
        setValue(`primary_dataResourceName_${attrgroupKey}`, true);
      }
    };

    const [idOptionsList, setIdOptionsList] = React.useState(
      ensureRequiredIdOptions(data?.ID_options_list)
    );
    const handleChangeIDOptions = (selected: any) => {
      const requiredSelected = ensureRequiredIdOptions(selected);
      setValue("ID_options_selecte", requiredSelected.join(","), {
        shouldDirty: true,
        shouldTouch: true,
      });
      setIdOptionsList(requiredSelected);
    };
    const [entryTypeOptionsList, setEntryTypeOptionsList] = React.useState(
      data?.entrytype_options_list?.split(",") || []
    );
    const handleChangeEntryTypeOptions = (selected: any) => {
      setValue("entrytype_options_selecte", selected, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setEntryTypeOptionsList(selected?.split(","));
    };
    return (
      <>
        <TableContainer
          component={Paper}
          sx={{
            minWidth: 300,
            width: "100%",
            maxWidth: "100%",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "80px" }}>Select</TableCell>
                <TableCell sx={{ width: "80px" }}>Group</TableCell>
                <TableCell sx={{}}>Attributes</TableCell>
                <TableCell sx={{ width: "10%" }}>Type</TableCell>
              </TableRow>
              {/* <TableRow>
                <TableCell colSpan={4} sx={{ backgroundColor: "#e0e0e0" }}>
                  <Typography variant="subtitle2">
                    {dataResourceAttrs.join(",")}
                  </Typography>
                </TableCell>
              </TableRow> */}
            </TableHead>
            <TableBody>
              {Object.entries(attributeData as Record<string, any>).map(
                ([attrgroup, group]) => {
                  const isRequiredAttr =
                    isPrimaryBasicPersonalInfo &&
                    (attrgroup == "Birth Date" || attrgroup == "Name");
                  if (
                    !attrgroup ||
                    (attrgroup == "default" &&
                      group?.title !== "Financial Info")
                    // group?.title !== "Mother's Maiden Name"
                  ) {
                    return null;
                  }

                  return (
                    <TableRow
                      key={attrgroup}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        backgroundColor: "#fff",
                      }}
                    >
                      <TableCell>
                        <Controller
                          name={`primary_dataresources_${attrgroupKey}_attrs_${attrgroup}`}
                          control={control}
                          // defaultValue={false}
                          //查看dataResourceAttrs中是否有attrgroup, 如果是Required, Optional, 则设置为true
                          defaultValue={
                            isRequiredAttr ||
                            dataResourceAttrs?.some((item: any) =>
                              item.startsWith(attrgroup)
                            )
                          }
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => {
                                    if (isRequiredAttr && !e.target.checked) {
                                      e.target.checked = true;
                                    }
                                    field.onChange(e.target.checked);
                                    if (e.target.checked) {
                                      setValue(
                                        `primary_dataresources_${attrgroupKey}_required_${attrgroup}`,
                                        "Required"
                                      );
                                    } else {
                                      setValue(
                                        `primary_dataresources_${attrgroupKey}_required_${attrgroup}`,
                                        ""
                                      );
                                    }
                                    handleAttrChange(
                                      attrgroup,
                                      e.target.checked
                                    );
                                  }}
                                  disabled={isRequiredAttr}
                                />
                              }
                              label={`${attrgroup}`}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {attrgroup !== "default" &&
                          (attrgroup === "Business Cash Flows"
                            ? "Cash Flows"
                            : attrgroup)}
                      </TableCell>
                      <TableCell>
                        <ShowAttrGroupTitle
                          attrgroup={attrgroup}
                          group={group}
                          division={division}
                        />
                        <Box>
                          {attrgroup == "ID" && (
                            <>
                              <CustomProductCheckboxGroup
                                selected={idOptionsList}
                                handleChange={handleChangeIDOptions}
                                requiredSelectedIds={["ssn"]}
                                optionlist={[
                                  {
                                    id: "ssn",
                                    label: "Social Security Number (SSN)",
                                  },
                                  {
                                    id: "tin",
                                    label: "Tax Identification Number (TIN)",
                                  },
                                  { id: "driver", label: "Driver License" },
                                  {
                                    id: "employmentauth",
                                    label:
                                      "Employment Authorization Document (Form I-766/EAD)",
                                  },
                                  {
                                    id: "traveldocument",
                                    label: "Form I-94 Travel Document",
                                  },
                                  { id: "passprot", label: "USA Passport" },
                                  {
                                    id: "non-usa-passport",
                                    label: "Non-USA Passport",
                                  },
                                  { id: "studentid", label: "Student ID" },
                                  {
                                    id: "idcard",
                                    label: "State or Municipal ID",
                                  },
                                  {
                                    id: "greencard",
                                    label:
                                      "US Permanent Resident Card (Green Card)",
                                  },
                                ]}
                              />
                            </>
                          )}
                          {attrgroup == "Entity Type" && (
                            <>
                              <CustomProductCheckboxGroup
                                selected={entryTypeOptionsList}
                                handleChange={handleChangeEntryTypeOptions}
                                optionlist={[
                                  {
                                    id: "partnership",
                                    label: "Partnership",
                                  },
                                  {
                                    id: "ccorp",
                                    label: "C-Corp",
                                  },
                                  {
                                    id: "scorp",
                                    label: "S-Corp",
                                  },
                                  {
                                    id: "llc",
                                    label: "LLC",
                                  },
                                  {
                                    id: "nonprofit",
                                    label: "501(c)(3) nonprofit",
                                  },
                                  {
                                    id: "veterans",
                                    label: "501(c)(19) veterans organization",
                                  },
                                  {
                                    id: "tribal",
                                    label:
                                      "Tribal business (sec. 31(b)(2)(C) of Small Business Act)",
                                  },
                                  {
                                    id: "sole",
                                    label: "Sole Proprietorship",
                                  },
                                  {
                                    id: "contractor",
                                    label: "Independent Contractor",
                                  },
                                  {
                                    id: "ownerllc",
                                    label: "Single-Owner LLC",
                                  },
                                ]}
                              />
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`primary_dataresources_${attrgroupKey}_required_${attrgroup}`}
                          control={control}
                          // //查看dataResourceAttrs中是否有attrgroup|Required 或者是attrgroup|Optional
                          // 分别设置required, optional
                          defaultValue={
                            isRequiredAttr
                              ? "Required"
                              : dataResourceAttrs?.some((item: any) =>
                                  item.startsWith(attrgroup)
                                )
                              ? dataResourceAttrs
                                  .find((item: any) =>
                                    item.startsWith(attrgroup)
                                  )
                                  ?.split("|")[1]
                              : ""
                          }
                          render={({ field }) => (
                            <CustomProductSelect
                              {...field}
                              disabled={isRequiredAttr}
                              optionlist={[
                                { value: "", label: "Select" },
                                { value: "Required", label: "Required" },
                                { value: "Optional", label: "Optional" },
                              ]}
                              onChange={(e) => {
                                if (isRequiredAttr) {
                                  field.onChange("Required");
                                  handleAttrChange(attrgroup, true);
                                  return;
                                }
                                field.onChange(e);
                                if (
                                  e.target.value === "Required" ||
                                  e.target.value == "Optional"
                                ) {
                                  setValue(
                                    `primary_dataresources_${attrgroupKey}_attrs_${attrgroup}`,
                                    true
                                  );
                                  handleAttrChange(attrgroup, true);
                                } else {
                                  setValue(
                                    `primary_dataresources_${attrgroupKey}_attrs_${attrgroup}`,
                                    false
                                  );
                                }
                              }}
                              small
                            />
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const DataFieldGrid = ({
    fieldKey: key,
    value,
    dataType,
    division,
    control,
    owner = "no",
  }: any) => {
    const [showDetail, setShowDetail] = React.useState(false);
    const [selectState, setSelectState] = React.useState(
      normalizeDelimitedValues(data?.statesData)
    );
    const [fieldGroups, setFieldGroups] = React.useState([]);
    const isRequiredPrimaryBasicPersonalInfo =
      owner !== "yes" && key === "base_personal_info";
    const normalizedAttrGroupValue = ensureRequiredAttrEntries(
      key,
      value?.attrsgroup
    );
    const dataResourceFieldName =
      owner == "yes"
        ? `owner_dataResourceName_${key}`
        : `primary_dataResourceName_${key}`;
    const defaultSelected =
      isRequiredPrimaryBasicPersonalInfo ||
      (division == "Business"
        ? value?.business_selected === "yes" || value?.owner_selected === "yes"
        : value?.myinfo_selected === "yes");
    const isDetailEnabled = useWatch({
      control,
      name: dataResourceFieldName,
      defaultValue: defaultSelected,
    });

    const {
      setIds = [],
      title = "",
      desc: description = "",
      appto = [],
    } = value || {};

    // Filter out based on dataType and division
    if (division != "Business") {
      if (
        dataType === "myinfo" &&
        !appto.includes("myinfo") &&
        !appto.includes("business")
      ) {
        return null;
      }
    }
    if (division === "Business" && key === "householdinfo") {
      return null;
    }

    const handleGetFieldGroups = async () => {
      const response = await sendRequest(
        `getDataTypeFields/primaryApplicant/${key}`,
        { id: 0 }
      );
      if (response?.code === 200) {
        setFieldGroups(response?.attributeData || []);
      }
    };

    const toggleDetail = async () => {
      // setShowDetail(!showDetail);
      // if (!showDetail) handleGetFieldGroups();
      // 如果showDetail为false, 则获取数据, 然后展示，否则不获取
      if (!showDetail) {
        await handleGetFieldGroups();
        setShowDetail(true);
      } else {
        setShowDetail(false);
      }
    };

    React.useEffect(() => {
      if (!isDetailEnabled && showDetail) {
        setShowDetail(false);
      }
    }, [isDetailEnabled, showDetail]);

    const handleChangeState = (selected: any) => {
      const normalizedStates = normalizeDelimitedValues(selected);
      setSelectState(normalizedStates);
      setValue("states", normalizedStates.join("|"), {
        shouldDirty: true,
        shouldTouch: true,
      });
    };
    const renderFieldSpecificContent = () => {
      switch (key) {
        case "address":
          return (
            <>
              Residential Address for the most recent
              <Controller
                name="min_month_address"
                control={control}
                defaultValue={data?.iMinMonthsAddress ?? 0}
                render={({ field }) => (
                  <CustomProductIntTextField {...field} small />
                )}
              />
              months <br />
              Permissible States :
              <StateModal
                selected={selectState}
                handleChange={handleChangeState}
              />
              <Controller
                name="states"
                control={control}
                defaultValue={normalizeDelimitedValues(data?.statesData).join(
                  "|"
                )}
                render={({ field }) => (
                  <CustomProductTextField {...field} readOnly={true} />
                )}
              />
            </>
          );
        case "employmentinfo":
          return (
            <>
              Employment Status, Employer and Employment Income Info for the
              most recent
              <Controller
                name="min_months_employment"
                control={control}
                defaultValue={data?.iMinMonthsEmployment ?? 0}
                render={({ field }) => (
                  <CustomProductIntTextField {...field} small />
                )}
              />
              months
            </>
          );
        case "reference":
          return (
            <>
              <div style={{ display: "inline-block", marginRight: "10px" }}>
                <Controller
                  name="referral"
                  control={control}
                  defaultValue={data?.iNumReferrals ?? 0}
                  render={({ field }) => (
                    <CustomProductIntTextField {...field} small />
                  )}
                />
              </div>
              Minimum number of references required
            </>
          );
        default:
          return (
            <Typography variant="body2" color="textSecondary">
              {description}
            </Typography>
          );
      }
    };

    return (
      <>
        <Grid
          item
          xs={12}
          sx={{ borderBottom: "1px solid #ccc", paddingBottom: "10px" }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={1}>
              {/* <>{key}</> */}
              <Controller
                name={dataResourceFieldName}
                control={control}
                // defaultValue={value?.myinfo_selected === "yes"}
                defaultValue={defaultSelected}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        disabled={isRequiredPrimaryBasicPersonalInfo}
                        // onChange={(e) => field.onChange(e.target.checked)}
                        onChange={(e) => {
                          if (
                            isRequiredPrimaryBasicPersonalInfo &&
                            !e.target.checked
                          ) {
                            field.onChange(true);
                            return;
                          }
                          field.onChange(e.target.checked);
                          if (!e.target.checked) {
                            setShowDetail(false);
                          }
                        }}
                      />
                    }
                    label=""
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <FieldTitle title={title} />
            </Grid>
            <Grid item xs={7}>
              {renderFieldSpecificContent()}
            </Grid>
            <Grid item xs={1}>
              {key != "referralinfo" &&
                key != "financial_education_verification" && (
                  <>
                    {isDetailEnabled ? (
                      <IconButton onClick={toggleDetail}>
                        {showDetail ? (
                          <ExpandMoreIcon sx={{ color: "#1976d2" }} />
                        ) : (
                          <KeyboardArrowRightIcon sx={{ color: "#1976d2" }} />
                        )}
                      </IconButton>
                    ) : (
                      <IconButton>
                        <Tooltip
                          title="Please select the checkbox to view the details"
                          placement="left"
                        >
                          <KeyboardArrowRightIcon sx={{ color: "grey" }} />
                        </Tooltip>
                      </IconButton>
                    )}
                  </>
                )}
              <Controller
                name={`dataResourceAttrs_${key}`}
                control={control}
                defaultValue={normalizedAttrGroupValue}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <Controller
                name={`dataResourceAttrs_${key}_allRequired`}
                control={control}
                defaultValue={value?.attrsgroupAllRequired}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              {/* <Box sx={{ color: "red" }}>attrsgroup: {value?.attrsgroup}</Box>
              <Box sx={{ color: "blue" }}>
                attrsgroupAllRequired: {value?.attrsgroupAllRequired}
              </Box> */}
            </Grid>
          </Grid>
          <Collapse in={showDetail} timeout="auto" unmountOnExit>
            <Grid container>
              <Grid item xs={4}></Grid>
              <Grid item xs={7}>
                <DataFieldsBoxTable
                  attrgroupKey={key}
                  attrgroupDefaultAttrs={normalizedAttrGroupValue}
                  attrgroupDefaultAllRequiredAttrs={
                    value?.attrsgroupAllRequired
                  }
                  attributeData={fieldGroups}
                  control={control}
                  setValue={setValue}
                />
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
          </Collapse>
        </Grid>
        {/* {appto.length > 0 &&
          (appto.includes("myinfo") || appto.includes("business")) && (
            <Grid item xs={12} key={key}>
              {key} - {setIds.length > 0 ? setIds.join(", ") : "No Set IDs"}
            </Grid>
          )} */}
      </>
    );
  };

  const handleSaveClick = async () => {
    setIsSaving(true);

    const formData = getValues();
    const dataResourceName: string[] = []; //选中的数组
    const coapp_dataResourceName: string[] = []; //选中的数组
    const guarantors_dataResourceName: string[] = []; //选中的数组
    const owner_dataResourceName: string[] = []; //选中的数组

    //查询以primary_dataResourceName_开头的所有字段，如果是true，则加入数组
    Object.keys(formData).forEach((key) => {
      if (key.startsWith("primary_dataResourceName_")) {
        const fieldKey = key.replace("primary_dataResourceName_", "");
        if (formData[key]) {
          dataResourceName.push(fieldKey);
        }
      }
      if (key.startsWith("guarantor_dataResourceName_")) {
        const fieldKey = key.replace("guarantor_dataResourceName_", "");
        if (formData[key]) {
          guarantors_dataResourceName.push(fieldKey);
        }
      }
      if (key.startsWith("coapp_dataResourceName_")) {
        const fieldKey = key.replace("coapp_dataResourceName_", "");
        if (formData[key]) {
          coapp_dataResourceName.push(fieldKey);
        }
      }
      if (key.startsWith("owner_dataResourceName_")) {
        const fieldKey = key.replace("owner_dataResourceName_", "");
        if (formData[key]) {
          owner_dataResourceName.push(fieldKey);
        }
      }
    });
    formData.dataResourceName = dataResourceName;
    formData.coapp_dataResourceName = coapp_dataResourceName;
    formData.guarantors_dataResourceName = guarantors_dataResourceName;
    formData.owner_dataResourceName = owner_dataResourceName;
    formData.saveAction = sectionName;
    console.log("save formData===>", formData);
    try {
      await Promise.resolve(props?.onSave?.(formData));
      reset(formData);
    } finally {
      setIsSaving(false);
    }
  };

  const [questionList, setQuestionList] = React.useState(questionData);

  const QuestionAnswer = () => {
    const handleAddQuestionClick = () => {
      //add a new question
      let newAttrId = `${Math.random().toString(36).substr(2, 9)}`;
      const newQuestion = {
        // attrId: "new_" + (questionList.length + 1),
        // attrId 不能重复
        attrId: `new_${newAttrId}`,
        name: `questionsapplicationId_${newAttrId}`,
        title: "",
        htmltype: "textfield",
        required: "Required",
        order: questionList.length + 1,
      };
      setQuestionList((prev: any) => [...prev, newQuestion]);
    };
    const handleRemove = (removeAttrId: any) => {
      // 将当前这个Grid 删除
      const updatedQuestionList = questionList.filter(
        (item: any) => item.attrId !== removeAttrId
      );
      setQuestionList(updatedQuestionList);
      // 删除对应的值
      Object.keys(getValues()).forEach((key) => {
        if (key.startsWith(`questionsapplicationAttrId_${removeAttrId}`)) {
          setValue(key, "");
        }
        if (key.startsWith(`questionsapplicationId_${removeAttrId}`)) {
          setValue(key, "");
        }
        if (key.startsWith(`questionapplicationTitle_${removeAttrId}`)) {
          setValue(key, "");
        }
        if (key.startsWith(`questionsdatavalue_${removeAttrId}`)) {
          setValue(key, "");
        }
        if (key.startsWith(`questionapplicationType_${removeAttrId}`)) {
          setValue(key, "");
        }
        if (key.startsWith(`questionsrequired_${removeAttrId}`)) {
          setValue(key, "");
        }
        if (key.startsWith(`questionsapplicationOrder_${removeAttrId}`)) {
          setValue(key, "");
        }
      });
    };

    const QuestionLine = ({ ...props }) => {
      const item = props.item;
      let defaultShowItemVal =
        item?.htmltype == "radio" || item?.htmltype == "checkbox";
      const [showItemVal, setShowItemVal] = React.useState(
        defaultShowItemVal ? true : false
      );
      return (
        <Grid container sx={{ borderBottom: "1px solid #eee", pt: 2, pb: 2 }}>
          <Grid item xs={1} sx={{ textAlign: "right" }}>
            <HelpOutlineIcon sx={{ color: "#1976d2", mt: 1, mr: 1 }} />
          </Grid>
          <Grid item xs={6}>
            {/* {item?.attrId}--{item?.htmltype} */}
            <Controller
              name={`questionsapplicationAttrId_${item?.attrId}`}
              control={control}
              defaultValue={item?.attrId}
              render={({ field }) => <input type="hidden" {...field} />}
            />
            <Controller
              name={`questionsapplicationId_${item?.name}`}
              control={control}
              defaultValue={item?.name}
              render={({ field }) => <input type="hidden" {...field} />}
            />
            <Controller
              name={`questionapplicationTitle_${item?.attrId}`}
              control={control}
              defaultValue={item?.title}
              render={({ field }) => (
                <CustomProductTextField
                  {...field}
                  fullWidth
                  sx={{ marginTop: 0 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: "5px", pr: "5px" }}>
            <Controller
              name={`questionapplicationType_${item?.attrId}`}
              control={control}
              defaultValue={
                item?.htmltype == "checkbox" && item?.single
                  ? "radio"
                  : item?.htmltype
              }
              render={({ field }) => (
                <CustomProductSelect
                  {...field}
                  optionlist={[
                    { value: "textfield", label: "Short Answer" },
                    { value: "textarea", label: "Long Answer" },
                    { value: "radio", label: "Single Choice" },
                    { value: "checkbox", label: "Multiple Choice" },
                  ]}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (
                      e.target.value == "radio" ||
                      e.target.value == "checkbox"
                    ) {
                      setShowItemVal(true);
                    } else {
                      setShowItemVal(false);
                    }
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: "5px", pr: "5px" }}>
            <Controller
              name={`questionsrequired_${item?.attrId}`}
              control={control}
              defaultValue={item?.required}
              render={({ field }) => (
                <CustomProductSelect
                  {...field}
                  optionlist={[
                    { value: "Required", label: "Required" },
                    { value: "Optional", label: "Optional" },
                  ]}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={1}>
            <CustomEditRemoveIcon onClick={() => handleRemove(item?.attrId)} />
          </Grid>
          <Grid item xs={7}>
            &nbsp;
          </Grid>
          <Grid item xs={5} sx={{ pl: "5px", pr: "5px" }}>
            {showItemVal && (
              <Controller
                name={`questionsdatavalue_${item?.attrId}`}
                control={control}
                defaultValue={item?.value}
                render={({ field }) => (
                  <CustomProductTextField
                    {...field}
                    label='Please fill in the options and separate them with "|||"'
                  />
                )}
              />
            )}
          </Grid>
        </Grid>
      );
    };
    return (
      <>
        <Box sx={{ width: "100%", mt: 2, mb: 2, p: 2 }}>
          {Object.entries(questionList).map(([key, item]) => {
            return (
              <>
                <QuestionLine key={key} item={item} />
                {/* <Grid container>
                  <Grid item xs={1}>
                    {item?.attrId}
                    <Controller
                      name={`questionsapplicationAttrId_${item?.attrId}`}
                      control={control}
                      defaultValue={item?.attrId}
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                    <Controller
                      name={`questionsapplicationId_${item?.name}`}
                      control={control}
                      defaultValue={item?.name}
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name={`questionapplicationTitle_${item?.attrId}`}
                      control={control}
                      defaultValue={item?.title}
                      render={({ field }) => (
                        <CustomProductTextField
                          {...field}
                          fullWidth
                          sx={{ marginTop: 0 }}
                        />
                      )}
                    />

                    <Controller
                      name={`questionsdatavalue_${item?.attrId}`}
                      control={control}
                      defaultValue={item?.value}
                      render={({ field }) => (
                        <CustomProductTextField {...field} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ pl: "5px", pr: "5px" }}>
                    <Controller
                      name={`questionapplicationType_${item?.attrId}`}
                      control={control}
                      defaultValue={item?.htmltype}
                      render={({ field }) => (
                        <CustomProductSelect
                          {...field}
                          optionlist={[
                            { value: "textfield", label: "Short Answer" },
                            { value: "textarea", label: "Long Answer" },
                            { value: "radio", label: "Single Choice" },
                            { value: "checkbox", label: "Multiple Choice" },
                          ]}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ pl: "5px", pr: "5px" }}>
                    <Controller
                      name={`questionsrequired_${item?.attrId}`}
                      control={control}
                      defaultValue={item?.required}
                      render={({ field }) => (
                        <CustomProductSelect
                          {...field}
                          optionlist={[
                            { value: "Required", label: "Required" },
                            { value: "Optional", label: "Optional" },
                          ]}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <CustomEditRemoveIcon
                      onClick={() => handleRemove(item?.attrId)}
                    />
                  </Grid>
                </Grid> */}
              </>
            );
          })}
        </Box>
        <Grid container>
          <Button
            variant="outlined"
            onClick={handleAddQuestionClick}
            fullWidth
            sx={{
              mt: 2,
              ml: 2,
              width: "150px",
              textTransform: "none",
            }}
          >
            Add Question
          </Button>
        </Grid>
      </>
    );
  };
  return (
    <SectionStyled id={`section_${sectionName}`}>
      <Controller
        name="ID_options_selecte"
        control={control}
        defaultValue={ensureRequiredIdOptions(data?.ID_options_list).join(",")}
        render={({ field }) => <input type="hidden" {...field} />}
      />
      <Controller
        name="entrytype_options_selecte"
        control={control}
        defaultValue={data?.entrytype_options_list ?? ""}
        render={({ field }) => <input type="hidden" {...field} />}
      />
      <SectionTitle subtitle={sectionSubtitle} title={sectionTitle} />
      <Box
        sx={{
          padding: "10px",
          height: "calc(100vh - 255px)",
          overflowY: "auto",
          // background: "yellow",
        }}
      >
        <Grid container sx={{ backgroundColor: "#f5f5f5", padding: "10px" }}>
          <FieldTitle
            title={
              division === "Business"
                ? "Business Applicant"
                : "Primary Applicant"
            }
          />
        </Grid>
        <Box sx={{ padding: "10px" }}>
          {division == "Business" ? (
            <Grid container spacing={2}>
              {Object.entries(dataResourceSettings).map(([key, value]) => (
                <>
                  {(key == "business_base_personal_info" ||
                    key == "business_address" ||
                    key == "business_operation" ||
                    key == "business_financialinfo" ||
                    key == "business_referralinfo") && (
                    <StableDataFieldGrid
                      key={key}
                      fieldKey={key}
                      value={value}
                      dataType="myinfo"
                      division={division}
                      control={control}
                      data={data}
                      getValues={getValues}
                      setValue={setValue}
                    />
                  )}
                </>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {Object.entries(dataResourceSettings).map(([key, value]) => (
                <>
                  {key !== "business_base_personal_info" &&
                    key != "business_address" &&
                    key != "business_operation" &&
                    key != "business_financialinfo" &&
                    key != "business_referralinfo" && (
                      <StableDataFieldGrid
                        key={key}
                        fieldKey={key}
                        value={value}
                        dataType="myinfo"
                        division={division}
                        control={control}
                        data={data}
                        getValues={getValues}
                        setValue={setValue}
                      />
                    )}
                </>
              ))}
            </Grid>
          )}
        </Box>
        {productType == "smallbusiness" && (
          <>
            <Grid
              container
              sx={{ backgroundColor: "#f5f5f5", padding: "10px" }}
            >
              <FieldTitle title={"Business Info"} />
            </Grid>
            <Box sx={{ padding: "10px" }}>
              <Grid container spacing={2}>
                {Object.entries(dataResourceSettings).map(([key, value]) => (
                  <>
                    {(key == "business_base_personal_info" ||
                      key == "business_address" ||
                      key == "business_operation" ||
                      key == "business_financialinfo") && (
                      <StableDataFieldGrid
                        key={key}
                        fieldKey={key}
                        value={value}
                        dataType="myinfo"
                        division={division}
                        control={control}
                        data={data}
                        getValues={getValues}
                        setValue={setValue}
                      />
                    )}
                  </>
                ))}
              </Grid>
            </Box>
          </>
        )}
        {/* Co-Application */}
        {division != "Business" && productType != "smallbusiness" && (
          <>
            <Grid
              container
              sx={{ backgroundColor: "#f5f5f5", padding: "10px" }}
            >
              <Grid item xs={1}>
                <FieldTitle title={"Co-Applicants"} />
              </Grid>
              <Grid item xs={11}>
                <Controller
                  name="cosigner"
                  control={control}
                  defaultValue={data?.sCoSigner ?? "NO"}
                  render={({ field }) => (
                    <CustomProductSelect
                      {...field}
                      optionlist={[
                        { value: "YES", label: "Accepted" },
                        { value: "NO", label: "NOT Accepted" },
                      ]}
                      // onChange={(e) => field.onChange(e.target.value)}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        if (e.target.value === "YES") {
                        } else {
                          setValue(
                            "coapp_dataResourceName_base_personal_info",
                            false
                          );
                          setValue("coapp_dataResourceName_address", false);
                          setValue(
                            "coapp_dataResourceName_employmentinfo",
                            false
                          );
                        }
                      }}
                      small
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box sx={{ padding: "10px" }}>
              <Grid container spacing={2}>
                {defaultGroupCoApplicant.map((item) => (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "10px",
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={1}>
                        <Controller
                          name={`coapp_dataResourceName_${item.key}`}
                          control={control}
                          defaultValue={
                            item.key === "base_personal_info"
                              ? item?.coapp_selected === "yes" ||
                                coSignerAccepted
                              : item?.coapp_selected === "yes"
                          }
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={
                                    item.key === "base_personal_info"
                                      ? coSignerAccepted || field.value
                                      : field.value
                                  }
                                  disabled={
                                    item.key === "base_personal_info" &&
                                    coSignerAccepted
                                  }
                                  // onChange={(e) => field.onChange(e.target.checked)}
                                  onChange={(e) => {
                                    if (
                                      item.key === "base_personal_info" &&
                                      coSignerAccepted &&
                                      !e.target.checked
                                    ) {
                                      field.onChange(true);
                                      return;
                                    }
                                    field.onChange(e.target.checked);
                                    if (e.target.checked) {
                                      setValue("cosigner", "YES");
                                    }
                                  }}
                                />
                              }
                              label=""
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FieldTitle title={item.title} />
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2" color="textSecondary">
                          {item.desc}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}></Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}

        {/* Owner */}
        {division == "Business" && (
          <>
            <Grid
              container
              sx={{ backgroundColor: "#f5f5f5", padding: "10px" }}
            >
              <Grid item xs={2}>
                <FieldTitle title={"Owner or Principal"} />
              </Grid>
              <Grid item xs={10}>
                <Controller
                  name="sOwner"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <CustomProductSelect
                      {...field}
                      optionlist={[
                        { value: "YES", label: "Accepted" },
                        { value: "NO", label: "NOT Accepted" },
                      ]}
                      // onChange={(e) => field.onChange(e.target.value)}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        if (e.target.value === "YES") {
                        } else {
                          // setValue(
                          //   "owner_dataResourceName_base_personal_info",
                          //   false
                          // );
                          // setValue("owner_dataResourceName_address", false);
                          // setValue(
                          //   "owner_dataResourceName_employmentinfo",
                          //   false
                          // );
                          // setValue("owner_dataResourceName_reference", false);
                        }
                      }}
                      small
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box sx={{ padding: "10px" }}>
              <Grid container spacing={2}>
                {Object.entries(
                  dataResourceSettings as Record<string, any>
                ).map(([key, value]) => (
                  <>
                    {value?.appto.includes("owner") && (
                      <StableDataFieldGrid
                        key={key}
                        fieldKey={key}
                        value={value}
                        dataType="myinfo"
                        owner="yes"
                        division={division}
                        control={control}
                        data={data}
                        getValues={getValues}
                        setValue={setValue}
                      />
                    )}
                  </>
                ))}
              </Grid>
            </Box>
          </>
        )}
        {/* Guarantor */}
        <Grid container sx={{ backgroundColor: "#f5f5f5", padding: "10px" }}>
          <Grid item xs={1}>
            <FieldTitle title={"Guarantor"} />
          </Grid>
          <Grid item xs={11}>
            <Controller
              name="guarantor"
              control={control}
              defaultValue={data?.sGuarantor ?? "NO"}
              render={({ field }) => (
                <CustomProductSelect
                  {...field}
                  optionlist={[
                    { value: "YES", label: "Accepted" },
                    { value: "NO", label: "NOT Accepted" },
                  ]}
                  // onChange={(e) => field.onChange(e.target.value)}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value === "YES") {
                    } else {
                      setValue(
                        "guarantor_dataResourceName_base_personal_info",
                        false
                      );
                      setValue("guarantor_dataResourceName_address", false);
                      setValue(
                        "guarantor_dataResourceName_employmentinfo",
                        false
                      );
                      setValue("guarantor_dataResourceName_reference", false);
                    }
                  }}
                  small
                />
              )}
            />
          </Grid>
        </Grid>
        <Box sx={{ padding: "10px" }}>
          <Grid container spacing={2}>
            {defaultGroupGuarantor.map((item) => (
              <Grid
                item
                xs={12}
                sx={{ borderBottom: "1px solid #ccc", paddingBottom: "10px" }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={1}>
                    <Controller
                      name={`guarantor_dataResourceName_${item.key}`}
                      control={control}
                      defaultValue={
                        item.key === "base_personal_info"
                          ? item?.guarantor_selected === "yes" ||
                            guarantorAccepted
                          : item?.guarantor_selected === "yes"
                      }
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={
                                item.key === "base_personal_info"
                                  ? guarantorAccepted || field.value
                                  : field.value
                              }
                              disabled={
                                item.key === "base_personal_info" &&
                                guarantorAccepted
                              }
                              // onChange={(e) => field.onChange(e.target.checked)}
                              onChange={(e) => {
                                if (
                                  item.key === "base_personal_info" &&
                                  guarantorAccepted &&
                                  !e.target.checked
                                ) {
                                  field.onChange(true);
                                  return;
                                }
                                field.onChange(e.target.checked);
                                if (e.target.checked) {
                                  setValue("guarantor", "YES");
                                }
                              }}
                            />
                          }
                          label=""
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FieldTitle title={item.title} />
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2" color="textSecondary">
                      {item.desc}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}></Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Additional Questions */}
        <Grid container sx={{ backgroundColor: "#f5f5f5", padding: "10px" }}>
          <Grid item xs={2}>
            <FieldTitle title={"Additional Questions"} />
          </Grid>
          <Grid item xs={10}>
            <Controller
              name="sQuestion"
              control={control}
              defaultValue={data?.sQuestion ?? "NO"}
              render={({ field }) => (
                <CustomProductSelect
                  {...field}
                  optionlist={[
                    { value: "YES", label: "Accepted" },
                    { value: "NO", label: "NOT Accepted" },
                  ]}
                  // onChange={(e) => field.onChange(e.target.value)}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  small
                />
              )}
            />
          </Grid>
        </Grid>
        <Box sx={{ padding: "10px" }}>
          <Grid container spacing={2}>
            <QuestionAnswer />
          </Grid>
        </Box>
      </Box>
      <CustomProductActionBottom>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleSaveClick)}
          disabled={!isDirty || isSaving}
          // disabled={!isActualDirty}
          sx={{ width: "120px", mt: 1 }}
        >
          {isSaving ? (
            <>
              Saving <CircularProgress size={16} color="inherit" />
            </>
          ) : (
            "Save"
          )}
        </Button>
      </CustomProductActionBottom>
    </SectionStyled>
  );
}
